import { getBackendUri } from "@/lib/backendSource";

export type EmulatorPerson = {
  id: string;
  pgaId: string | null;
  displayName: string;
};

export type EmulatorCall = {
  id: string;
  callSid: string;
  participantPhone: string | null;
  direction: string | null;
  callStatus: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  transcriptSummaryStatus: string;
  transcriptSummary: string | null;
  interactionId: string | null;
  person: EmulatorPerson | null;
};

export type SampleCaller = {
  phone: string;
  person: EmulatorPerson;
};

export type EmulatorTranscriptResult = {
  call: EmulatorCall;
  scheduled: boolean;
  scheduledInSeconds: number;
};

export type EmulatorDirection = "inbound" | "outbound-api";

export const EMULATOR_TRANSCRIPT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "unavailable", label: "Unavailable" }
];

export const EMULATOR_BACKEND_NOT_ENABLED_MESSAGE =
  "The Flex emulator is not enabled on this backend. Point Member 360 at a local Rails server running in development.";

export class FlexEmulatorError extends Error {
  status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = "FlexEmulatorError";
    this.status = status;
  }
}

export const isEmulatorNotEnabledError = (error: unknown): boolean =>
  error instanceof FlexEmulatorError && error.status === 404 && /not enabled/i.test(error.message);

export type EmulatorRequestOptions = {
  getAccessToken: () => Promise<string | undefined>;
  signal?: AbortSignal;
};

const extractErrorText = (payload: unknown, status: number): string => {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const message = record.error ?? record.errors;
    if (typeof message === "string" && message.trim()) return message;
    if (Array.isArray(message) && message.length > 0) return message.map(String).join("; ");
    if (message && typeof message === "object") return JSON.stringify(message);
  }

  return `Request failed with status ${status}`;
};

async function emulatorRequest<T>(
  path: string,
  options: EmulatorRequestOptions & { method?: string; body?: unknown }
): Promise<T> {
  const { getAccessToken, signal, method = "GET", body } = options;
  const backendUri = getBackendUri();
  if (!backendUri) {
    throw new FlexEmulatorError("Backend URI not configured", null);
  }

  const authToken = await getAccessToken();
  if (!authToken) {
    throw new FlexEmulatorError("Authentication required. Please log in.", null);
  }

  let response: Response;
  try {
    response = await fetch(`${backendUri}/dev/flex_emulator${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal
    });
  } catch (err) {
    if (signal?.aborted) {
      throw new FlexEmulatorError("Request cancelled.", null);
    }
    throw new FlexEmulatorError(
      err instanceof Error ? err.message : "Failed to reach the backend. Please try again.",
      null
    );
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new FlexEmulatorError(extractErrorText(payload, response.status), response.status);
  }

  return payload as T;
}

export async function fetchRecentCalls(options: EmulatorRequestOptions & { limit?: number }): Promise<EmulatorCall[]> {
  const { limit = 10, ...requestOptions } = options;
  const payload = await emulatorRequest<{ calls: EmulatorCall[] }>(`/calls?limit=${limit}`, requestOptions);
  return payload.calls ?? [];
}

export async function fetchEmulatorCall(callSid: string, options: EmulatorRequestOptions): Promise<EmulatorCall> {
  const payload = await emulatorRequest<{ call: EmulatorCall }>(`/calls/${encodeURIComponent(callSid)}`, options);
  return payload.call;
}

export async function startEmulatorCall(
  input: { phone: string; direction: EmulatorDirection },
  options: EmulatorRequestOptions
): Promise<EmulatorCall> {
  const payload = await emulatorRequest<{ call: EmulatorCall }>("/calls", {
    ...options,
    method: "POST",
    body: input
  });
  return payload.call;
}

export async function completeEmulatorCall(
  callSid: string,
  input: { durationSeconds?: number; phone?: string; direction?: EmulatorDirection } | undefined,
  options: EmulatorRequestOptions
): Promise<EmulatorCall> {
  // Rails reads snake_case keys; serialize explicitly rather than passing the
  // camelCase input through.
  const body: Record<string, unknown> = {};
  if (input?.durationSeconds !== undefined) body.duration_seconds = input.durationSeconds;
  if (input?.phone !== undefined) body.phone = input.phone;
  if (input?.direction !== undefined) body.direction = input.direction;
  const payload = await emulatorRequest<{ call: EmulatorCall }>(`/calls/${encodeURIComponent(callSid)}/complete`, {
    ...options,
    method: "POST",
    body
  });
  return payload.call;
}

export async function setEmulatorTranscript(
  callSid: string,
  input: { status: string; summary?: string; delaySeconds?: number },
  options: EmulatorRequestOptions
): Promise<EmulatorTranscriptResult> {
  const body: Record<string, unknown> = { status: input.status };
  if (input.summary !== undefined) body.summary = input.summary;
  if (input.delaySeconds !== undefined) body.delay_seconds = input.delaySeconds;
  return emulatorRequest<EmulatorTranscriptResult>(`/calls/${encodeURIComponent(callSid)}/transcript`, {
    ...options,
    method: "POST",
    body
  });
}

export async function fetchSampleCaller(
  options: EmulatorRequestOptions
): Promise<{ caller: SampleCaller | null; hint?: string }> {
  return emulatorRequest<{ caller: SampleCaller | null; hint?: string }>("/sample_caller", options);
}
